/**
 * 261002 송출 transport 재생성 감시.
 *
 * 왜 있나: iceRestartCoordinator 는 한 번 connected 된 transport 의 disconnected / failed 만 다룬다.
 *          송출 fallback 이 transport 를 다시 세운 뒤 두 경우에는 조정자를 깨울 이벤트가 하나도 없다.
 *          ① 새 transport 가 망이 끊긴 채 connecting 에서 멈춤 — connectionstatechange 가 더 오지 않는다
 *          ② MQ 까지 끊겨 재생성 요청이 사라짐 — 응답이 없으니 새 transport 도, 새 조정자도 생기지 않는다
 *          그러면 송출이 영영 안 돌아온다(교사 격자에 「화면 끊김」만 남는다). 수신 쪽은 같은 빈틈을
 *          LIVE_RECONNECT_WATCH_MS 감시로 막았다(r15110 병합, 20261002). 이것은 그 송출판이다.
 *          ②가 있으므로 transport 가 생길 때가 아니라 **재생성 요청을 보낼 때** 건다.
 *
 * 송신에는 [다시 연결] 버튼이 없으므로 횟수 상한을 두지 않는다 — 다시 걸지는 만료 콜백이 정한다.
 * 첫 참여 때는 걸지 않는다(카메라 권한 창 등으로 produce 가 늦어지면 오판한다). 부르는 쪽 몫이다.
 *
 * 이 모듈은 판단만 한다 — 전역도 import 도 없다. 만료 때 할 일은 주입된 콜백이다.
 */

/**
 * 감시 간격. 수신 감시(LIVE_RECONNECT_WATCH_MS)와 같은 눈금이다 —
 * 그룹 20개가 한꺼번에 끊길 때 재생성이 서버에 몰리는 부담을 줄이려고 수신 쪽이 25초에서 40초로 늘렸다.
 * 정상 연결은 1초 안쪽이라(Pro 실기 66·67차) 성공을 자르지 않는다.
 */
export const SEND_RECREATE_WATCH_MS = 40000;

/**
 * 감시가 만료돼 포기할 때의 이유. Android 는 callRestartProducerTransportFailed(reason) 로 네이티브에 그대로 보낸다
 * (로그용, 처리는 다른 이유와 같다). ⚠ Android 와 합의한 목록(iceRestartCoordinator REASON)에 더한 이름이다.
 */
export const SEND_CONNECT_TIMEOUT_REASON = "connect-timeout";

/**
 * @param {Object}   deps
 * @param {Function} deps.onTimeout - (reason) → 감시 시간 안에 연결되지 않았다. 다시 세우고 필요하면 다시 건다
 */
export const createSendRecreateWatch = ({ onTimeout }) => {
    let timer = null;

    const clear = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const arm = (reason) => {
        clear();
        // 261027 Pro 97차: 거는 줄이 없어 「감시 0건」이 안 걸린 것인지 걸렸다가 풀린 것인지 가를 수 없었다.
        console.log(`sendRecreateWatch - 감시 건다(${reason}) ${SEND_RECREATE_WATCH_MS / 1000}초`);
        timer = setTimeout(() => {
            timer = null;
            onTimeout(reason);
        }, SEND_RECREATE_WATCH_MS);
    };

    return {
        /** 재생성을 요청했다. 이미 걸려 있으면 처음부터 다시 잰다. */
        arm,

        /** 송출 transport 가 connected 됐다 */
        connected: () => {
            if (timer !== null) console.log("sendRecreateWatch - 송출이 붙었다. 감시 푼다.");
            clear();
        },

        /**
         * 261027 MQ 가 다시 열렸다 — 아직 기다리는 중(감시가 걸려 있음)이면 재생성 요청을 한 번 더 보내고 처음부터 다시 잰다.
         * 앞선 요청이 MQ 가 닫힌 사이 버려졌을 수 있다(Pro 97-1 · Android pro-rbmqSendDropped). 만료(40초)까지 기다리지 않는다.
         * 걸려 있지 않으면(이미 붙었거나 내렸거나 재생성한 적이 없음) 아무것도 하지 않는다 — 다시 보내면 송출이 겹친다.
         * @returns {Boolean} 다시 보냈으면 true
         */
        resendIfWaiting: (reason, resend) => {
            if (timer === null) {
                console.log(`sendRecreateWatch - 기다리는 재생성이 없다. 다시 보내지 않는다(${reason}).`);
                return false;
            }
            console.log(`sendRecreateWatch - 기다리는 중이라 재생성 요청을 다시 보낸다(${reason}).`);
            resend();
            arm(reason);
            return true;
        },

        /** 송출을 내렸다(unpublish · 손들기 종료) */
        cancel: () => {
            if (timer !== null) console.log("sendRecreateWatch - 송출을 내렸다. 감시 푼다.");
            clear();
        },

        isArmed: () => timer !== null
    };
}
